import { pubsub } from '@services';

import { isActive, nextQuery } from './utils/plpFilterChips/index.mjs';

/**
 * PLP quick-filter chips (NGE-11501).
 *
 * The chips are anchors carrying a query string — `?filter.v.price.lte=20`,
 * `?sort_by=created-descending`. Left as plain anchors they navigate: full
 * reload, scroll thrown back to the top, and no chip ever looked selected
 * afterwards, because the section can only mark a chip active from
 * `current_tags` and a query-string chip carries no tag. Two of STIT's three
 * chips did change the grid server-side; the customer could not tell.
 *
 * Same hand-off the filter drawer already uses (`filter-form.js`): pushState,
 * then publish `filters:updated`, which <collection-grid> is subscribed to.
 * Active state is read back from the URL instead of rendered, so it holds for
 * any chip shape without the section having to understand the params.
 *
 * Progressive: with no JS the anchors still navigate and the server still
 * applies the params, exactly as before.
 */
class PlpFilterChips extends HTMLElement {
  connectedCallback() {
    this.chips = [...this.querySelectorAll('.plp-filter-chip')];
    if (this.chips.length === 0) return;

    this.onClick = (event) => this.#handleClick(event);
    this.onPopState = () => this.#syncActive();

    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);

    // The drawer and the sort dropdown move the same params this chip reads, and
    // neither reloads the page — so a chip has to re-read the URL when they fire
    // too, or "Sotto i 20 €" stays unlit after the drawer applies that filter.
    this.unsubscribeFiltersUpdated = pubsub.subscribe('filters:updated', () => this.#syncActive());
    this.unsubscribeSortChanged = pubsub.subscribe('sort:changed', () => this.#syncActive());

    this.#syncActive();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
    this.unsubscribeFiltersUpdated?.();
    this.unsubscribeSortChanged?.();
  }

  #handleClick(event) {
    const chip = event.target.closest('.plp-filter-chip');
    if (!chip || !this.contains(chip)) return;

    // A modified click (cmd/ctrl/shift/alt, or a non-primary button) asks for a
    // new tab or window — that is the anchor's job, not ours. On live today the
    // chips are plain anchors and cmd+click works; hijacking it here would be a
    // regression the toggle logic never needs.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;

    // A tag chip is a path, not a query (/collections/x/tag) — a real
    // navigation the grid's section fetch cannot express, so leave it alone.
    // Once the shopper is ON that tag page the pathname matches, which is why
    // the empty query string is what identifies it, not the path.
    const url = new URL(chip.href, window.location.origin);
    if (!url.search || url.pathname !== window.location.pathname) return;

    event.preventDefault();

    const query = nextQuery(url.search, window.location.search);
    const { pathname } = window.location;
    history.pushState({}, '', query ? `${pathname}?${query}` : pathname);

    pubsub.publish('filters:updated');
    this.#syncActive();
  }

  #syncActive() {
    for (const chip of this.chips) {
      const url = new URL(chip.href, window.location.origin);
      // Tag chips keep the server-rendered state — it is resolved from
      // `current_tags`, which the URL params say nothing about. On the tag's own
      // page the pathname matches, so without the empty-query test this would
      // overwrite the server's `aria-pressed="true"` with false.
      if (!url.search || url.pathname !== window.location.pathname) continue;

      chip.setAttribute('aria-pressed', String(isActive(url.search, window.location.search)));
    }
  }
}

customElements.define('plp-filter-chips', PlpFilterChips);
